// packages/shared/api-client/endpoints/unionHall.js
// Union hall reservation API endpoints

import { baseApi } from "../baseApi";

const unionHallApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // ---- Spaces ----

    getHallSpaces: builder.query({
      query: () => "/api/unionHall/spaces",
      providesTags: [{ type: "HallSpace", id: "LIST" }],
    }),

    createHallSpace: builder.mutation({
      query: (data) => ({ url: "/api/unionHall/spaces", method: "POST", body: data }),
      invalidatesTags: [{ type: "HallSpace", id: "LIST" }],
    }),

    updateHallSpace: builder.mutation({
      query: ({ id, ...data }) => ({ url: `/api/unionHall/spaces/${id}`, method: "PUT", body: data }),
      invalidatesTags: [{ type: "HallSpace", id: "LIST" }],
    }),

    deleteHallSpace: builder.mutation({
      query: (id) => ({ url: `/api/unionHall/spaces/${id}`, method: "DELETE" }),
      invalidatesTags: [{ type: "HallSpace", id: "LIST" }],
    }),

    // ---- Series ----

    createHallReservationSeries: builder.mutation({
      query: (data) => ({ url: "/api/unionHall/series", method: "POST", body: data }),
      invalidatesTags: [
        { type: "Reservation", id: "LIST" },
        { type: "Reservation", id: "MY_RESERVATIONS" },
      ],
    }),

    cancelHallReservationSeries: builder.mutation({
      query: (seriesId) => ({ url: `/api/unionHall/series/${seriesId}`, method: "DELETE" }),
      invalidatesTags: [
        { type: "Reservation", id: "LIST" },
        { type: "Reservation", id: "MY_RESERVATIONS" },
      ],
    }),

    // ---- Single reservations ----

    getHallReservations: builder.query({
      query: (params = {}) => ({ url: "/api/unionHall", params }),
      providesTags: (result) =>
        result?.data
          ? [
              ...result.data.map(({ id }) => ({ type: "Reservation", id })),
              { type: "Reservation", id: "LIST" },
            ]
          : [{ type: "Reservation", id: "LIST" }],
    }),

    getMyReservations: builder.query({
      query: (params = {}) => ({ url: "/api/unionHall/my-reservations", params }),
      providesTags: (result) =>
        result?.data
          ? [
              ...result.data.map(({ id }) => ({ type: "Reservation", id })),
              { type: "Reservation", id: "MY_RESERVATIONS" },
            ]
          : [{ type: "Reservation", id: "MY_RESERVATIONS" }],
    }),

    checkHallAvailability: builder.query({
      query: ({ startTime, endTime, spaceId, excludeReservationId } = {}) => ({
        url: "/api/unionHall/availability",
        params: {
          startTime,
          endTime,
          spaceId,
          ...(excludeReservationId && { excludeReservationId }),
        },
      }),
    }),

    getHallReservationById: builder.query({
      query: (id) => `/api/unionHall/${id}`,
      providesTags: (result, error, id) => [{ type: "Reservation", id }],
    }),

    createHallReservation: builder.mutation({
      query: (data) => ({ url: "/api/unionHall", method: "POST", body: data }),
      invalidatesTags: [
        { type: "Reservation", id: "LIST" },
        { type: "Reservation", id: "MY_RESERVATIONS" },
      ],
    }),

    updateHallReservation: builder.mutation({
      query: ({ id, ...data }) => ({ url: `/api/unionHall/${id}`, method: "PUT", body: data }),
      invalidatesTags: (result, error, { id }) => [
        { type: "Reservation", id },
        { type: "Reservation", id: "LIST" },
        { type: "Reservation", id: "MY_RESERVATIONS" },
      ],
    }),

    deleteHallReservation: builder.mutation({
      query: (id) => ({ url: `/api/unionHall/${id}`, method: "DELETE" }),
      invalidatesTags: [
        { type: "Reservation", id: "LIST" },
        { type: "Reservation", id: "MY_RESERVATIONS" },
      ],
    }),

    approveHallReservation: builder.mutation({
      query: ({ id, reviewNotes }) => ({
        url: `/api/unionHall/${id}/approve`,
        method: "POST",
        body: reviewNotes ? { reviewNotes } : {},
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: "Reservation", id },
        { type: "Reservation", id: "LIST" },
      ],
    }),

    rejectHallReservation: builder.mutation({
      query: ({ id, reviewNotes }) => ({
        url: `/api/unionHall/${id}/reject`,
        method: "POST",
        body: { reviewNotes },
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: "Reservation", id },
        { type: "Reservation", id: "LIST" },
      ],
    }),
  }),
});

export const useGetHallSpacesQuery = unionHallApi.useGetHallSpacesQuery;
export const useCreateHallSpaceMutation = unionHallApi.useCreateHallSpaceMutation;
export const useUpdateHallSpaceMutation = unionHallApi.useUpdateHallSpaceMutation;
export const useDeleteHallSpaceMutation = unionHallApi.useDeleteHallSpaceMutation;
export const useCreateHallReservationSeriesMutation = unionHallApi.useCreateHallReservationSeriesMutation;
export const useCancelHallReservationSeriesMutation = unionHallApi.useCancelHallReservationSeriesMutation;
export const useGetHallReservationsQuery = unionHallApi.useGetHallReservationsQuery;
export const useGetMyReservationsQuery = unionHallApi.useGetMyReservationsQuery;
export const useCheckHallAvailabilityQuery = unionHallApi.useCheckHallAvailabilityQuery;
export const useGetHallReservationByIdQuery = unionHallApi.useGetHallReservationByIdQuery;
export const useCreateHallReservationMutation = unionHallApi.useCreateHallReservationMutation;
export const useUpdateHallReservationMutation = unionHallApi.useUpdateHallReservationMutation;
export const useDeleteHallReservationMutation = unionHallApi.useDeleteHallReservationMutation;
export const useApproveHallReservationMutation = unionHallApi.useApproveHallReservationMutation;
export const useRejectHallReservationMutation = unionHallApi.useRejectHallReservationMutation;
